import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { isEmpty } from 'validate.js';

import Icon from 'dpl/common/components/Icon';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import SmartLink from 'dpl/components/SmartLink';
import { PAYMENT_PROTECTION_POLICY_PATH } from 'dpl/shared/constants/urls';
import useToggle from 'dpl/shared/hooks/useToggle';
import { capitalize } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import KlarnaDescription from './KlarnaDescription';

const IS_LT_MD = isLessThanBreakpoint('md');

function PriceSection({ className, heading, subheading, amount, content }) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);

  return (
    <div
      className={classnames(
        'PriceSection flex flex-column gap-16 font-16 pv6',
        className
      )}
    >
      <div className="flex justify-between gap-24 lh-title">
        <div className="flex items-center gap-8 flex-wrap">
          <span className="fw-medium">{heading}</span>
          <div className="flex items-center gap-8">
            {subheading && <span className="tertiary">{subheading}</span>}
            {!isEmpty(content) && (
              <button
                type="button"
                className={classnames(
                  'bg-gray-200 br-100',
                  isExpanded ? 'padding-h-6 padding-v-6' : 'ph1 pv1'
                )}
                style={{ height: '28px', width: '28px' }}
                onClick={toggleIsExpanded}
              >
                <Icon
                  name={isExpanded ? 'fetch-close' : 'fetch-info'}
                  className="secondary"
                  height="100%"
                  width="100%"
                />
              </button>
            )}
          </div>
        </div>
        <span className="fw-medium nowrap">{amount}</span>
      </div>
      {isExpanded && <div className="font-14 tertiary">{content}</div>}
    </div>
  );
}

PriceSection.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  amount: PropTypes.string.isRequired,
  content: PropTypes.node
};

PriceSection.defaultProps = {
  className: null,
  subheading: null,
  content: null
};

function IncludedExtrasBanner({ paragraphs }) {
  return (
    <div className="IncludedExtrasBanner flex flex-column gap-8 font-14 bg-teal-light br3 pv3 ph3">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  );
}

IncludedExtrasBanner.propTypes = {
  paragraphs: PropTypes.arrayOf(PropTypes.string).isRequired
};

export function PriceBreakdownContent({
  className,
  deposit,
  depositDescription,
  depositRefundPolicy,
  dogPayment,
  subtotal,
  isBnplAvailable
}) {
  const items = useMemo(() => {
    return [
      deposit && {
        heading: 'Deposit',
        amount: deposit,
        subheading: depositRefundPolicy
          ? capitalize(depositRefundPolicy)
          : null,
        content: depositDescription ? (
          <p>Breeder’s policy: “{depositDescription}”</p>
        ) : null
      },
      dogPayment && {
        heading: 'Dog payment',
        amount: dogPayment,
        subheading: isBnplAvailable ? <KlarnaDescription as="span" /> : null
      },
      subtotal && {
        heading: 'Subtotal',
        amount: subtotal,
        subheading: 'Before taxes & fees',
        content: (
          <>
            State and local taxes are calculated at payment based on your
            puppy’s pickup location. Fees cover processing and platform costs,
            and ensure your payment is protected with{' '}
            <SmartLink
              className="stone-700 underline"
              to={PAYMENT_PROTECTION_POLICY_PATH}
              target="_blank"
            >
              Good Dog Payment Protection
            </SmartLink>
            .
          </>
        )
      }
    ].filter(Boolean);
  }, []);

  return (
    <div className={classnames('PriceBreakdownContent', className)}>
      <ul>
        {items.map((item, index) => (
          <PriceSection
            key={index}
            className={classnames({ 'bt b--gray-300': index })}
            {...item}
          />
        ))}
      </ul>
    </div>
  );
}

PriceBreakdownContent.propTypes = {
  className: PropTypes.string,
  deposit: PropTypes.string,
  depositDescription: PropTypes.string,
  depositRefundPolicy: PropTypes.string,
  dogPayment: PropTypes.string,
  subtotal: PropTypes.string.isRequired,
  isBnplAvailable: PropTypes.bool
};

PriceBreakdownContent.defaultProps = {
  className: null,
  deposit: null,
  depositDescription: null,
  depositRefundPolicy: null,
  dogPayment: null,
  isBnplAvailable: false
};

export default function PriceBreakdownModal({
  includedExtrasParagraphs,
  isOpen,
  onClose,
  ...contentProps
}) {
  function Heading() {
    return <h3 className="font-24 lh-title">Price breakdown</h3>;
  }

  if (IS_LT_MD) {
    return (
      <FixedTooltipPortal
        className="PriceBreakdownModal pt6"
        isVisible={isOpen}
        onCloseClick={onClose}
      >
        <div
          className="no-scrollbars overflow-y-auto"
          style={{ maxHeight: '80vh' }}
        >
          <Heading />
          <PriceBreakdownContent {...contentProps} />
          {!isEmpty(includedExtrasParagraphs) && (
            <IncludedExtrasBanner paragraphs={includedExtrasParagraphs} />
          )}
        </div>
      </FixedTooltipPortal>
    );
  }

  if (!isOpen) return null;

  return (
    <MiniOverlayModal
      className="PriceBreakdownModal"
      closeButtonClassName="ba b--light-gray bg-white br-100 padding-h-6 padding-v-6 mt5 mt4-md mr4"
      contentClassName="pv8 ph8 bg-white"
      onCloseClick={onClose}
      maxWidth="600px"
    >
      <Heading />
      <PriceBreakdownContent {...contentProps} />
      {!isEmpty(includedExtrasParagraphs) && (
        <IncludedExtrasBanner paragraphs={includedExtrasParagraphs} />
      )}
    </MiniOverlayModal>
  );
}

PriceBreakdownModal.propTypes = {
  ...PriceBreakdownContent.propTypes,
  includedExtrasParagraphs: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

PriceBreakdownModal.defaultProps = {
  ...PriceBreakdownContent.defaultProps,
  includedExtrasParagraphs: null
};
